import clsx from 'clsx';
import React from 'react';

type ButtonVariant = 'primary' | 'danger' | 'secondary' | 'outline' | 'ghost' | 'text' | 'icon' | 'dangerText';

type ButtonProps = {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
  variant?: ButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'text-white',
  danger: 'text-white bg-[#CC4747]',
  secondary: 'bg-[#F4F4F4] text-[#113D7B]',
  outline: 'bg-transparent text-[#4A5565] underline',
  ghost: 'bg-transparent text-[#4A5565] underline',
  text: 'bg-transparent text-[#113D7B]',
  icon: 'bg-transparent',
  dangerText: 'text-red-500',
};

const variantHoverStyles: Record<ButtonVariant, string> = {
  primary: 'hover:brightness-[0.88] hover:shadow-sm',
  danger: 'hover:brightness-95 hover:shadow-sm',
  secondary: 'hover:bg-[#ECECEC] hover:shadow-sm',
  outline: 'hover:opacity-80',
  ghost: 'hover:opacity-80',
  text: 'hover:opacity-80',
  icon: 'hover:opacity-60',
  dangerText: 'hover:opacity-80',
};

export const Button: React.FC<ButtonProps> = ({
  onClick,
  children,
  variant = 'primary',
  disabled = false,
  loading = false,
  className,
}) => {
  const isDisabled = disabled || loading;
  const isIcon = variant === 'icon';

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={clsx(
        'inline-flex items-center justify-center border-none',
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
        loading && 'opacity-70',

        isIcon ? 'p-0 rounded-none gap-0' : 'px-4 py-2.5 rounded-[20px] gap-2',

        variant === 'ghost' ? 'text-xs font-normal' : !isIcon && 'text-sm font-medium',

        variantStyles[variant],
        !isDisabled && variantHoverStyles[variant],
        variant === 'primary' && (isDisabled ? 'bg-[#B8C5D7]' : 'bg-[linear-gradient(128deg,#113D7B_0%,#0E4EA8_100%)]'),

        className
      )}
    >
      {loading ? 'Loading...' : children}
    </button>
  );
};
